/**
 * useToolBrowserStore — dedicated Zustand store for the tool sidebar browser.
 *
 * Owns sidebar-specific state: view mode, search query, expanded groups,
 * favorites, recently used tools, and view mode preference.
 *
 * Phase 5 — Unit 5.1
 */

import { create } from 'zustand';
import { createJSONStorage,persist } from 'zustand/middleware';

export type ToolSidebarMode = 'all' | 'favorites' | 'pipelines';
export type ToolBrowserViewMode = 'categorized' | 'flat' | 'grid';

interface RecentToolEntry {
  toolId: string;
  usedAt: number;
}

interface ToolBrowserState {
  /** Current sidebar view mode */
  mode: ToolSidebarMode;
  /** Search query string */
  query: string;
  /** Set of expanded tool group names */
  expandedGroups: Set<string>;
  /** Favorited tool IDs */
  favorites: string[];
  /** Recently used tools with timestamps */
  recentTools: RecentToolEntry[];
  /** Maximum recent tools to track */
  maxRecent: number;
  /** Current browse view layout */
  viewMode: ToolBrowserViewMode;

  setMode: (mode: ToolSidebarMode) => void;
  setQuery: (query: string) => void;
  toggleGroup: (group: string) => void;
  setExpandedGroups: (groups: Set<string>) => void;

  /** Add a tool to favorites. No-op if already favorited. */
  addFavorite: (toolId: string) => void;
  /** Remove a tool from favorites. No-op if not favorited. */
  removeFavorite: (toolId: string) => void;
  /** Toggle a tool's favorite status. */
  toggleFavorite: (toolId: string) => void;
  /** Record that a tool was just used. Moves it to the front of recents. */
  recordToolUse: (toolId: string) => void;
  /** Set the browse view layout. */
  setViewMode: (viewMode: ToolBrowserViewMode) => void;
  /** Clear recent tools list. */
  clearRecents: () => void;
  /** Reset all browser state to initial values. */
  reset: () => void;
}

export const useToolBrowserStore = create<ToolBrowserState>()(
  persist(
    (set, get) => ({
      mode: 'all',
      query: '',
      expandedGroups: new Set(),
      favorites: [],
      recentTools: [],
      maxRecent: 20,
      viewMode: 'categorized' as ToolBrowserViewMode,

      setMode: (mode) => set({ mode }),
      setQuery: (query) => set({ query }),
      toggleGroup: (group) =>
        set((s) => {
          const next = new Set(s.expandedGroups);
          if (next.has(group)) next.delete(group);
          else next.add(group);
          return { expandedGroups: next };
        }),
      setExpandedGroups: (groups) => set({ expandedGroups: groups }),

      addFavorite: (toolId) => {
        const { favorites } = get();
        if (!favorites.includes(toolId)) {
          set({ favorites: [...favorites, toolId] });
        }
      },

      removeFavorite: (toolId) => {
        set((s) => ({ favorites: s.favorites.filter((id) => id !== toolId) }));
      },

      toggleFavorite: (toolId) => {
        const { favorites } = get();
        if (favorites.includes(toolId)) {
          set({ favorites: favorites.filter((id) => id !== toolId) });
        } else {
          set({ favorites: [...favorites, toolId] });
        }
      },

      recordToolUse: (toolId) => {
        const { recentTools, maxRecent } = get();
        const filtered = recentTools.filter((r) => r.toolId !== toolId);
        const updated = [{ toolId, usedAt: Date.now() }, ...filtered].slice(0, maxRecent);
        set({ recentTools: updated });
      },

      setViewMode: (viewMode) => set({ viewMode }),

      clearRecents: () => set({ recentTools: [] }),

      reset: () => set({
        mode: 'all',
        query: '',
        expandedGroups: new Set(),
        favorites: [],
        recentTools: [],
        maxRecent: 20,
        viewMode: 'categorized',
      }),
    }),
    {
      name: 'swedevtools-tool-browser',
      version: 1,
      migrate: (persisted, _version) => {
        const defaults = { favorites: [], recentTools: [], viewMode: 'categorized' as const };
        return { ...defaults, ...(persisted as Record<string, unknown>) } as Partial<ToolBrowserState>;
      },
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        favorites: state.favorites,
        recentTools: state.recentTools,
        viewMode: state.viewMode,
      }),
    }
  )
);
